/**
 * Shared name validation function for user and group names.
 * Validates names according to backend security rules to prevent injection attacks.
 * 
 * @param {string} name - The name to validate
 * @param {string} fieldLabel - Label for the field (used in error messages)
 * @returns {Object} Object with 'valid' boolean and 'message' string
 */
export function validateName(name, fieldLabel = 'Name') {
    var trimmed = name ? name.trim() : '';

    if (!trimmed) {
        return { valid: false, message: fieldLabel + ' is required' };
    }

    // Detect control characters (newline, tab, form feed, carriage return, null, etc.)
    var controlCharPattern = /[\x00-\x1F\x7F]/;
    if (controlCharPattern.test(trimmed)) {
        return {
            valid: false,
            message: fieldLabel + ' cannot contain control characters (newlines, tabs, etc.)'
        };
    }

    if (trimmed.length < 2) {
        return { valid: false, message: fieldLabel + ' must be at least 2 characters' };
    }

    if (trimmed.length > 50) {
        return { valid: false, message: fieldLabel + ' must be less than 50 characters' };
    }

    var reservedWords = ['true', 'false', 'null', 'undefined', 'nan', 'infinity'];
    if (reservedWords.includes(trimmed.toLowerCase())) {
        return {
            valid: false,
            message: fieldLabel + ' cannot be a reserved word (true, false, null, etc.)'
        };
    }

    // Reject purely numeric values (including negative numbers)
    var purelyNumericPattern = /^-?\d+$/;
    if (purelyNumericPattern.test(trimmed)) {
        return { valid: false, message: fieldLabel + ' cannot be purely numeric' };
    }

    // Reject HTML markup to prevent HTML injection attacks
    var htmlTagPattern = /<[^>]*>/;
    if (htmlTagPattern.test(trimmed)) {
        return { valid: false, message: fieldLabel + ' cannot contain HTML markup' };
    }

    // Allow only letters (including accented), numbers, spaces, hyphens, apostrophes, and periods
    var validNamePattern = /^[a-zA-ZÀ-ÿ0-9\s\-'.]+$/;
    if (!validNamePattern.test(trimmed)) {
        return {
            valid: false,
            message:
                fieldLabel +
                ' can only contain letters, numbers, spaces, hyphens, apostrophes, and periods'
        };
    }

    return { valid: true, message: '' };
}
